/**
 * Issue a fiscal receipt (касова бележка) on the kiosk's built-in Datecs
 * SK1-21F, through the local datecs-bridge agent.
 *
 * Completely independent of the non-fiscal slip in printReceipt.js: an object
 * may want only the order slip, only the fiscal receipt, or both. The two are
 * driven by two separate settings and two separate COM ports.
 *
 * The server can never reach the printer, so the kiosk browser is the bridge in
 * both directions: it asks the local agent to print and then reports back what
 * happened, which is what stops a second fiscal receipt being issued for the
 * same order and what raises the alert when one is NOT issued.
 */

import { API_URL, DATECS_BRIDGE_URL } from '../../constants';
import Auth from '../../utils/Auth';
import Utils from '../../utils/Utils';

// Payment codes of the Datecs fiscal protocol (command 53).
const PAY_CASH = 'P';
const PAY_CARD = 'N';

// A fiscal receipt is a handful of short commands; this ceiling only exists so
// a wedged agent cannot hang the payment screen.
const FISCAL_TIMEOUT_MS = 20000;

/** Per-place switch. The receipt payload carries it too, for older kiosks. */
export function kioskFiscalEnabled(receipt) {
  if (receipt && receipt.fiscal && typeof receipt.fiscal.enabled === 'boolean') {
    return receipt.fiscal.enabled;
  }
  return String(Utils.getSetting('kiosk_fiscal_receipt_enabled')) === '1';
}

function paymentMode(receipt) {
  const m = String((receipt && receipt.paymentMethod) || '').toLowerCase();
  if (m === 'cash') return PAY_CASH;
  // Everything else at a kiosk is a card / online authorisation.
  return PAY_CARD;
}

/**
 * Turn the backend receipt into the line items the fiscal device registers.
 * The device multiplies unit price by quantity itself, so we must send the unit
 * price — `price` on the slip is the line total and would double-charge.
 */
function fiscalItems(receipt) {
  const items = Array.isArray(receipt && receipt.items) ? receipt.items : [];
  return items
    .map((it) => {
      const qty = Number(it.quantity) || 1;
      const unit =
        it.unitPrice != null ? Number(it.unitPrice) : Number(it.price) / (qty || 1);
      return {
        name: (it.isAddon ? '+ ' : '') + String(it.name || ''),
        size: it.size || '',
        quantity: qty,
        unitPrice: Number.isFinite(unit) ? Number(unit.toFixed(2)) : 0,
        taxGroup: it.taxGroup || (receipt.fiscal && receipt.fiscal.defaultTaxGroup) || null,
      };
    })
    .filter((it) => it.unitPrice > 0);
}

/** Tell the backend what happened — it logs it and alerts on failures. */
function report(orderUuid, ok, result, error) {
  try {
    return fetch(API_URL + 'kiosk/fiscal-receipt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        uuid: orderUuid,
        ok: ok,
        receipt: result || null,
        error: error || null,
      }),
    }).catch(() => {});
  } catch (e) {
    return Promise.resolve();
  }
}

/**
 * Print the fiscal receipt for an already-fetched order receipt.
 *
 * Always resolves — the order is already placed and paid, so a printer problem
 * must not strand the customer on a spinner. The failure is reported to the
 * backend (which emails the staff) and returned to the caller.
 *
 * @param {object} receipt   payload from GET /orders/:uuid/kiosk-receipt
 * @param {string} orderUuid
 * @returns {Promise<{printed:boolean, skipped?:string, error?:string, result?:object}>}
 */
export function maybePrintFiscalReceipt(receipt, orderUuid) {
  try {
    if (!receipt) return Promise.resolve({ printed: false, skipped: 'no receipt' });
    if (!Auth.isKioskMode()) return Promise.resolve({ printed: false, skipped: 'not kiosk' });
    if (!kioskFiscalEnabled(receipt)) {
      return Promise.resolve({ printed: false, skipped: 'disabled' });
    }
    // Never fiscalise money that has not been taken: cash-on-pickup and
    // on-the-bill orders are receipted where they are actually paid.
    if (!receipt.paid) return Promise.resolve({ printed: false, skipped: 'not paid' });
    // Already issued for this order (page refresh, retried confirm).
    if (receipt.fiscal && receipt.fiscal.printed) {
      return Promise.resolve({ printed: false, skipped: 'already printed' });
    }

    const items = fiscalItems(receipt);
    if (items.length === 0) {
      return Promise.resolve({ printed: false, skipped: 'no billable items' });
    }

    const payload = {
      ref: orderUuid || null,
      receipt: {
        items: items,
        // The charged amount wins: the bridge settles any difference against
        // the line sum as a discount/mark-up so the fiscal total matches.
        total: Number(receipt.total),
        payment: { mode: paymentMode(receipt) },
        footer: receipt.orderNumber ? ['Поръчка № ' + receipt.orderNumber] : [],
      },
    };

    return new Promise((resolve) => {
      let settled = false;
      const done = (out) => {
        if (settled) return;
        settled = true;
        if (out.error) console.warn('[kiosk-fiscal] ' + out.error);
        resolve(out);
      };
      const timer = setTimeout(() => {
        report(orderUuid, false, null, 'Фискалният принтер не отговори навреме');
        done({ printed: false, error: 'timeout' });
      }, FISCAL_TIMEOUT_MS);

      fetch(DATECS_BRIDGE_URL + '/fiscal/print', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
        .then((r) => r.json().then((d) => ({ ok: r.ok, d })).catch(() => ({ ok: r.ok, d: null })))
        .then(({ ok, d }) => {
          clearTimeout(timer);
          if (!ok || !d || d.ok !== true) {
            const error = (d && d.error) || 'фискалният принтер отказа бона';
            report(orderUuid, false, null, error);
            done({ printed: false, error: error });
            return;
          }
          report(orderUuid, true, d, null);
          done({ printed: true, result: d });
        })
        .catch(() => {
          clearTimeout(timer);
          report(orderUuid, false, null, 'Локалният агент е недостъпен');
          done({ printed: false, error: 'bridge unreachable' });
        });
    });
  } catch (e) {
    return Promise.resolve({ printed: false, error: e.message });
  }
}
